import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Layout,
  List,
  Page,
  PageActions,
  Tabs,
  Text,
} from "@shopify/polaris"
import {
  ConnectionStatus,
  ConnectionTrigger,
  Endpoint,
  PageRoute,
  ShopFrontend,
} from "../../typings/types"
import {
  ANNUAL_SYNC_COST_IN_DOLLARS,
  EXTRA_ARTICLE_COST_IN_CENTS,
  EXTRA_ARTICLE_COST_IN_DOLLARS,
  MONTHLY_CHARGE_CAP_IN_DOLLARS,
  MONTHLY_SYNC_COST_IN_DOLLARS,
  MONTHLY_SYNC_LIMIT,
} from "../../settings.json"
import { ShopContext, userLoggedInFetch } from "../App"
import { useContext, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { confirmUrl } from "../helpers/navigation"
import dayjs from "dayjs"
import { useAppBridge } from "@shopify/app-bridge-react"
import { useTranslation } from "react-i18next"

const Pricing = () => {
  const { t } = useTranslation()
  const app = useAppBridge()
  const fetch = userLoggedInFetch(app)
  const [shop, setShop] = useContext(ShopContext)
  const navigate = useNavigate()
  const location = useLocation()
  const [inviteDomain, setInvite] = useState<string>()
  const [loading, setLoading] = useState(false)
  const [billingInterval, setBillingInterval] = useState<"monthly" | "annual">(
    "monthly"
  )
  const isAnnual = billingInterval === "annual"

  const annualSavingsPercent = Math.round(
    ((MONTHLY_SYNC_COST_IN_DOLLARS * 12 - ANNUAL_SYNC_COST_IN_DOLLARS) /
      (MONTHLY_SYNC_COST_IN_DOLLARS * 12)) *
      100
  )
  const monthlyEquivalent = ANNUAL_SYNC_COST_IN_DOLLARS / 12

  useEffect(() => {
    const { search } = location
    const queryParams = new URLSearchParams(search)
    const inviteParam = queryParams.get("invite")

    // navigate(location.pathname, { replace: true })
    if (inviteParam) setInvite(inviteParam)
  }, [])

  const confirmPayment = async (automatic = true) => {
    setLoading(true)
    if (automatic) {
      const intervalParam = isAnnual ? "ANNUAL" : "EVERY_30_DAYS"
      const subscriptionUrl: string = await fetch(
        Endpoint.SUBSCRIPTION +
          `?returnUrl=${encodeURIComponent(
            confirmUrl({ trigger: ConnectionTrigger.AUTOMATIC, inviteDomain })
          )}&interval=${intervalParam}`
      ).then((res) => res?.text() || "")
      if (subscriptionUrl.length > 0) {
        window.open(subscriptionUrl, "_top")
        return
      } else {
        // throw new Error(
        //   "Something went wrong when trying to get the subscription URL"
        // )
        navigate("/")
      }
    } else {
      if (isAutomatic) {
        // switching from automatic to manual, cancel the subscription
        await fetch(Endpoint.SUBSCRIPTION, { method: "DELETE" })
        setShop(
          (prevShop) => ({ ...prevShop, billing_active: false } as ShopFrontend)
        )
        navigate("/")
      } else {
        navigate(PageRoute.CONFIRM, {
          state: {
            plan: automatic
              ? ConnectionTrigger.AUTOMATIC
              : ConnectionTrigger.MANUAL,
            invite: inviteDomain,
          },
        })
      }
    }
  }

  const parentConnection =
    !!shop && shop.connections.length > 0 && shop.connections[0]
  const currentTrigger =
    parentConnection &&
    parentConnection.status === ConnectionStatus.ACTIVE &&
    parentConnection.trigger
  const isAutomatic = currentTrigger === ConnectionTrigger.AUTOMATIC
  const isManual = currentTrigger === ConnectionTrigger.MANUAL

  return (
    <Page>
      <Layout>
        {isAutomatic && (
          <Layout.Section>
            <Banner
              tone="warning"
              title={t("pricing:automaticActiveWarning.title")}
            >
              <p>
                {t("pricing:automaticActiveWarning.message", {
                  d: dayjs(shop?.billing_last).add(30, "days").toDate(),
                  formatParams: {
                    d: { weekday: "long", month: "long", day: "numeric" },
                  },
                })}
              </p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Text variant="headingLg" as="h2">
            {t("pricing:title")}
          </Text>
          <Text tone="subdued" as="p">
            {inviteDomain
              ? t("pricing:info.installOnParent", { parent: inviteDomain })
              : t("pricing:info.onlyPayOnChild")}
          </Text>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <Card background={isAutomatic ? "bg-surface-secondary" : undefined}>
            <BlockStack gap="400">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                <Text variant="headingLg" as="h3">
                  {t("pricing:automatic.title")}
                </Text>
                <Tabs
                  fitted
                  tabs={[
                    { id: "monthly", content: t("pricing:tabs.monthly") },
                    {
                      id: "annual",
                      content: t("pricing:tabs.annual", {
                        savings: annualSavingsPercent,
                      }),
                    },
                  ]}
                  selected={isAnnual ? 1 : 0}
                  onSelect={(index) =>
                    setBillingInterval(index === 1 ? "annual" : "monthly")
                  }
                />
              </div>
              <InlineStack gap="600" wrap={false}>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="p">
                    {t("pricing:automatic.labels.monthly")}
                  </Text>
                  <Text variant="heading2xl" as="p">
                    {isAnnual
                      ? `$${Math.round(monthlyEquivalent)}`
                      : `$${MONTHLY_SYNC_COST_IN_DOLLARS}`}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="p">
                    {t("pricing:automatic.labels.postsIncluded")}
                  </Text>
                  <Text variant="heading2xl" as="p">
                    {MONTHLY_SYNC_LIMIT}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="p">
                    {t("pricing:automatic.labels.extraPost")}
                  </Text>
                  <Text variant="heading2xl" as="p">
                    ${EXTRA_ARTICLE_COST_IN_DOLLARS}
                  </Text>
                </BlockStack>
              </InlineStack>
              <List type="bullet">
                <List.Item>{t("pricing:automatic.bullet1")}</List.Item>
                <List.Item>
                  {t("pricing:automatic.bullet2", {
                    price: EXTRA_ARTICLE_COST_IN_CENTS / 100,
                    limit: MONTHLY_SYNC_LIMIT,
                  })}
                </List.Item>
                <List.Item>
                  {t("pricing:automatic.bullet3", {
                    maxLimit:
                      MONTHLY_SYNC_LIMIT +
                      MONTHLY_CHARGE_CAP_IN_DOLLARS /
                        EXTRA_ARTICLE_COST_IN_DOLLARS,
                  })}
                </List.Item>
              </List>
              <InlineStack align="end">
                <Button
                  variant="primary"
                  disabled={isAutomatic}
                  onClick={() => confirmPayment()}
                  loading={loading}
                >
                  {isAutomatic ? t("pricing:selected") : t("pricing:choose")}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <Card background={isManual ? "bg-surface-secondary" : undefined}>
            <BlockStack gap="400">
              <Text variant="headingLg" as="h3">
                {t("pricing:manual.title")}
              </Text>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued" as="p">
                  {t("pricing:manual.labels.perPost")}
                </Text>
                <Text variant="heading2xl" as="p">
                  ${EXTRA_ARTICLE_COST_IN_DOLLARS}
                </Text>
              </BlockStack>
              <List type="bullet">
                <List.Item>{t("pricing:manual.bullet1")}</List.Item>
              </List>
              <InlineStack align="end">
                <Button
                  variant="primary"
                  disabled={isManual}
                  loading={loading}
                  onClick={() => confirmPayment(false)}
                >
                  {isManual ? t("pricing:selected") : t("pricing:choose")}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
      <br />
      <PageActions
        // primaryAction={{
        //   content: "Save",
        // }}
        secondaryActions={[
          {
            content: t("common:cancel"),
            destructive: true,
            onAction: () => navigate("/"),
          },
        ]}
      />
    </Page>
  )
}

export default Pricing
